import { useState } from "react";
import { Loader2, ShieldCheck, AlertTriangle, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export function OtpStep({ mfa, onCancel }) {
  const { verifyOtp } = useAuth();
  const [backup, setBackup] = useState(mfa.email_failed);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = backup ? code.replace(/[\s-]/g, "").length === 10 : code.length === 6;
  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const d = await verifyOtp(mfa.challenge, code, remember);
      if (d.backup_codes_left !== undefined) toast.warning(`Backup code used — ${d.backup_codes_left} left. Generate new ones from the shield icon if you're running low.`);
    } catch (e2) {
      setErr(errMsg(e2));
      setCode("");
    } finally {
      setBusy(false);
    }
  };
  const resend = () => api.post("/auth/resend-otp", { challenge: mfa.challenge }).then(() => { toast.success("New code sent"); setBackup(false); }).catch((e) => toast.error(errMsg(e)));
  const [texted, setTexted] = useState("");
  const textMe = () => api.post("/auth/sms-otp", { challenge: mfa.challenge }).then((r) => { setTexted(r.data.mobile_hint); setBackup(false); setCode(""); toast.success(`Code sent by text to ${r.data.mobile_hint}`); }).catch((e) => toast.error(errMsg(e)));
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="otp-form">
      {texted ? (
        <div data-testid="otp-sms-sent" className="flex gap-3 rounded-lg bg-indigo-50 p-3 text-sm text-indigo-900">
          <MessageSquare className="h-5 w-5 shrink-0 text-indigo-600" /><span>We texted a new 6-digit code to <b>{texted}</b>. Earlier emailed codes no longer work.</span>
        </div>
      ) : mfa.email_failed ? (
        <div data-testid="otp-email-failed" className="flex gap-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" /><span>We couldn't email your code right now. Enter one of your backup codes, or try "Send a new code" in a minute.</span>
        </div>
      ) : (
        <div className="flex gap-3 rounded-lg bg-indigo-50 p-3 text-sm text-indigo-900">
          <ShieldCheck className="h-5 w-5 shrink-0 text-indigo-600" />
          <span>Two-step sign-in: we emailed a 6-digit code to <b data-testid="otp-email-hint">{mfa.email_hint}</b>. It expires in 10 minutes.</span>
        </div>
      )}
      {backup ? (
        <Input data-testid="otp-backup-input" placeholder="xxxxx-xxxxx" autoFocus value={code} onChange={(e) => setCode(e.target.value)} className="h-12 text-center font-mono text-lg tracking-widest" />
      ) : (
        <div className="flex justify-center"><InputOTP maxLength={6} value={code} onChange={setCode} data-testid="otp-input" autoFocus>
          <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg" />)}</InputOTPGroup>
        </InputOTP></div>
      )}
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" data-testid="otp-remember" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-indigo-600" />
        Remember this device for 30 days
      </label>
      {err && <div data-testid="otp-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      <Button data-testid="otp-submit" type="submit" className="w-full" disabled={busy || !ready}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verify and sign in</Button>
      <div className="flex flex-wrap justify-between gap-2 text-xs">
        <button type="button" data-testid="otp-resend" onClick={resend} className="font-medium text-indigo-600 hover:underline">Send a new code</button>
        {mfa.sms_available && <button type="button" data-testid="otp-sms" onClick={textMe} className="font-medium text-indigo-600 hover:underline">Text me a code instead</button>}
        <button type="button" data-testid="otp-toggle-backup" onClick={() => { setBackup(!backup); setCode(""); }} className="font-medium text-indigo-600 hover:underline">{backup ? "Use emailed code" : "Use a backup code"}</button>
        <button type="button" data-testid="otp-cancel" onClick={onCancel} className="text-slate-500 hover:underline">Use a different account</button>
      </div>
    </form>
  );
}
